import { createGlobalStyle } from 'styled-components';

export const GlobalStyle = createGlobalStyle`
  :root {
    /* Primary colors - Analytical blue-grey palette */
    --primary-50: #f3f4f8;
    --primary-100: #e7e9f1;
    --primary-200: #c3c8dc;
    --primary-300: #9fa7c7;
    --primary-400: #7885b2;
    --primary-500: #5464a0;
    --primary-600: #4a5a90;
    --primary-700: #3d4a78;
    --primary-800: #303a60;
    --primary-900: #1f2640;

    /* Accent colors - Warm intellect */
    --accent-orange: #ff8c42;
    --accent-coral: #ff6b6b;
    --accent-mint: #4ecdc4;
    --accent-purple: #a78bfa;
    --accent-gold: #f59e0b;

    /* Neutrals - Light theme optimized */
    --neutral-50: #ffffff;
    --neutral-100: #f9fafb;
    --neutral-200: #f3f4f6;
    --neutral-300: #e5e7eb;
    --neutral-400: #d1d5db;
    --neutral-500: #9ca3af;
    --neutral-600: #6b7280;
    --neutral-700: #4b5563;
    --neutral-800: #374151;
    --neutral-900: #1f2937;

    /* Typography */
    --font-serif: 'Georgia', 'Times New Roman', serif;
    --font-sans: -apple-system, BlinkMacSystemFont, 'Segoe UI', 'Roboto', 'Helvetica Neue', Arial, sans-serif;
    --font-mono: 'Menlo', 'Monaco', 'Courier New', monospace;

    /* Font sizes */
    --text-xs: 0.75rem;
    --text-sm: 0.875rem;
    --text-base: 1rem;
    --text-lg: 1.125rem;
    --text-xl: 1.25rem;
    --text-2xl: 1.5rem;
    --text-3xl: 1.875rem;
    --text-4xl: 2.25rem;
    --text-5xl: 3rem;

    /* Spacing */
    --space-1: 0.25rem;
    --space-2: 0.5rem;
    --space-3: 0.75rem;
    --space-4: 1rem;
    --space-5: 1.25rem;
    --space-6: 1.5rem;
    --space-8: 2rem;
    --space-10: 2.5rem;
    --space-12: 3rem;
    --space-16: 4rem;
    --space-20: 5rem;

    /* Border radius */
    --radius-sm: 0.125rem;
    --radius-md: 0.375rem;
    --radius-lg: 0.5rem;
    --radius-xl: 0.75rem;
    --radius-2xl: 1rem;
    --radius-full: 9999px;

    /* Shadows */
    --shadow-sm: 0 1px 2px 0 rgba(0, 0, 0, 0.05);
    --shadow: 0 1px 3px 0 rgba(0, 0, 0, 0.1), 0 1px 2px 0 rgba(0, 0, 0, 0.06);
    --shadow-md: 0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06);
    --shadow-lg: 0 10px 15px -3px rgba(0, 0, 0, 0.1), 0 4px 6px -2px rgba(0, 0, 0, 0.05);
    --shadow-xl: 0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04);

    /* Transitions */
    --transition-fast: 150ms ease;
    --transition-base: 250ms ease;
    --transition-slow: 350ms ease;

    /* Breakpoints */
    --breakpoint-sm: 640px;
    --breakpoint-md: 768px;
    --breakpoint-lg: 1024px;
    --breakpoint-xl: 1280px;
  }

  * {
    margin: 0;
    padding: 0;
    box-sizing: border-box;
  }

  html {
    font-size: 16px;
    -webkit-font-smoothing: antialiased;
    -moz-osx-font-smoothing: grayscale;
    scroll-behavior: smooth;
  }

  body {
    font-family: var(--font-sans);
    font-size: var(--text-base);
    line-height: 1.6;
    color: var(--neutral-900);
    background-color: var(--neutral-50);
    min-height: 100vh;
  }

  h1, h2, h3, h4, h5, h6 {
    font-family: var(--font-serif);
    font-weight: 700;
    line-height: 1.3;
    color: var(--neutral-900);
    margin-bottom: var(--space-4);
  }

  h1 {
    font-size: var(--text-4xl);
    @media (max-width: 768px) {
      font-size: var(--text-3xl);
    }
  }

  h2 {
    font-size: var(--text-3xl);
    @media (max-width: 768px) {
      font-size: var(--text-2xl);
    }
  }

  h3 {
    font-size: var(--text-2xl);
    @media (max-width: 768px) {
      font-size: var(--text-xl);
    }
  }

  h4 {
    font-size: var(--text-xl);
  }

  h5 {
    font-size: var(--text-lg);
  }

  h6 {
    font-size: var(--text-base);
  }

  p {
    margin-bottom: var(--space-4);
    color: var(--neutral-800);
  }

  a {
    color: var(--primary-600);
    text-decoration: none;
    transition: color var(--transition-fast);

    &:hover {
      color: var(--primary-700);
      text-decoration: underline;
    }
  }

  ul, ol {
    margin-bottom: var(--space-4);
    padding-left: var(--space-6);
  }

  li {
    margin-bottom: var(--space-2);
    color: var(--neutral-800);
  }

  code {
    font-family: var(--font-mono);
    font-size: 0.9em;
    background-color: var(--neutral-200);
    padding: 0.125rem 0.25rem;
    border-radius: var(--radius-sm);
  }

  pre {
    font-family: var(--font-mono);
    font-size: 0.9rem;
    background-color: var(--neutral-900);
    color: var(--neutral-100);
    padding: var(--space-4);
    border-radius: var(--radius-md);
    overflow-x: auto;
    margin-bottom: var(--space-4);
  }

  blockquote {
    border-left: 4px solid var(--primary-400);
    padding-left: var(--space-4);
    margin: var(--space-6) 0;
    font-style: italic;
    color: var(--neutral-700);
  }

  img {
    max-width: 100%;
    height: auto;
    display: block;
  }

  button {
    font-family: inherit;
    cursor: pointer;
    transition: all var(--transition-fast);
  }

  input, textarea, select {
    font-family: inherit;
    font-size: inherit;
  }

  ::selection {
    background-color: var(--primary-200);
    color: var(--primary-900);
  }

  /* Utility classes */
  .sr-only {
    position: absolute;
    width: 1px;
    height: 1px;
    padding: 0;
    margin: -1px;
    overflow: hidden;
    clip: rect(0, 0, 0, 0);
    white-space: nowrap;
    border-width: 0;
  }

  .container {
    max-width: 1200px;
    margin: 0 auto;
    padding: 0 var(--space-4);
  }

  .text-center {
    text-align: center;
  }

  .text-gradient {
    background: linear-gradient(135deg, var(--primary-600) 0%, var(--primary-800) 100%);
    -webkit-background-clip: text;
    -webkit-text-fill-color: transparent;
    background-clip: text;
  }

  /* Scrollbar styling */
  ::-webkit-scrollbar {
    width: 8px;
    height: 8px;
  }

  ::-webkit-scrollbar-track {
    background: var(--neutral-200);
  }

  ::-webkit-scrollbar-thumb {
    background: var(--neutral-400);
    border-radius: var(--radius-full);
  }

  ::-webkit-scrollbar-thumb:hover {
    background: var(--neutral-500);
  }

  /* Animation keyframes */
  @keyframes fadeIn {
    from {
      opacity: 0;
      transform: translateY(10px);
    }
    to {
      opacity: 1;
      transform: translateY(0);
    }
  }

  @keyframes slideIn {
    from {
      transform: translateX(-100%);
    }
    to {
      transform: translateX(0);
    }
  }

  @keyframes pulse {
    0%, 100% {
      opacity: 1;
    }
    50% {
      opacity: 0.5;
    }
  }
`;